import React from "react";
import { Link } from "react-router-dom";
import { GlyphRow } from "./Ornaments";
import type { LanguageBinding } from "@/hooks/useLanguage";
import { cx } from "@/lib/classnames";

const NAV = [
  { k: "home", path: "/" },
  { k: "book", path: "/book" },
  { k: "concepts", path: "/concepts" },
  { k: "videos", path: "/videos" },
  { k: "posts", path: "/posts" },
  { k: "english", path: "/english" },
  { k: "about", path: "/about" },
  { k: "legacy", path: "/legacy" },
  { k: "contact", path: "/contact" },
] as const;

export const Footer: React.FC<{ language: LanguageBinding }> = ({ language }) => {
  const { t, isRTL } = language;
  return (
    <footer className="relative" style={{ background: "var(--footer-bg)", color: "var(--footer-text)" }}>
      <div className="grain" />
      <div className="max-w-page mx-auto px-6 md:px-10 py-16 relative">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            {/* The sticky Nav already carries the avatar + author name at the
                top of every page. Footer keeps only the brand glyph row and a
                small note so the identity isn't duplicated. */}
            <div
              className={cx(isRTL ? "ff-ar-serif" : "ff-en-serif", "text-lg mb-2")}
              style={{ fontWeight: 600 }}
            >
              {t.footer.brand}
            </div>
            <div className="opacity-70" style={{ color: "var(--footer-muted)" }}>
              <GlyphRow color="var(--gold)" scale={0.75} />
            </div>
            <p
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-body", "mt-4 text-sm max-w-md")}
              style={{ color: "var(--footer-muted)" }}
            >
              {t.footer.note}
            </p>
          </div>

          <div className="md:col-span-4">
            <div
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-5")}
              style={{ color: "var(--vermillion-2)" }}
            >
              {t.footer.nav1}
            </div>
            <div className="grid grid-cols-2 gap-y-3">
              {NAV.map((it) => (
                <Link
                  key={it.k}
                  to={it.path}
                  className={cx("text-start", isRTL ? "ff-ar-sans" : "ff-en-body", "text-sm link-ul")}
                  style={{ color: "var(--footer-text)", opacity: 0.85 }}
                >
                  {t.nav[it.k]}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <div
              className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-widest", "text-[11px] mb-5")}
              style={{ color: "var(--vermillion-2)" }}
            >
              {t.footer.nav2}
            </div>
            <div className="space-y-3">
              <div>
                <div
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[10px] opacity-60")}
                >
                  {t.footer.email}
                </div>
                <Link
                  to="/contact"
                  className="ff-en-body text-sm link-ul"
                  style={{ color: "var(--footer-text)" }}
                >
                  {isRTL ? "نموذج التواصل" : "Contact form"}
                </Link>
              </div>
              <div>
                <div
                  className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "text-[10px] opacity-60")}
                >
                  {t.footer.social}
                </div>
                <div className="flex items-center gap-3 mt-1" style={{ color: "var(--footer-text)" }}>
                  <a
                    href="https://www.facebook.com/profile.php?id=100064762146555"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook page"
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ border: "1px solid var(--hairline-dark)" }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <path d="M8 14V8h2l.3-2.3H8V4.2c0-.7.2-1.1 1.1-1.1h1.2V1c-.2 0-.9-.1-1.7-.1-1.7 0-2.9 1-2.9 3v1.8H3.8V8h1.9v6H8z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/nabil.ytawfik"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram profile"
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ border: "1px solid var(--hairline-dark)" }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="2" width="20" height="20" rx="5" />
                      <circle cx="12" cy="12" r="4.2" />
                      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
                    </svg>
                  </a>
                  <a
                    href="https://www.youtube.com/playlist?list=PL1h2j-S1k8B2EJLMEdJ_1nMiQl7jtK6lg"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="YouTube playlist"
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ border: "1px solid var(--hairline-dark)" }}
                  >
                    <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor">
                      <path d="M15.6 1.9a2 2 0 00-1.4-1.4C13 .1 8 .1 8 .1s-5 0-6.2.4A2 2 0 00.4 1.9C0 3.1 0 6 0 6s0 2.9.4 4.1a2 2 0 001.4 1.4c1.2.4 6.2.4 6.2.4s5 0 6.2-.4a2 2 0 001.4-1.4c.4-1.2.4-4.1.4-4.1s0-2.9-.4-4.1zM6.4 8.6V3.4L10.8 6 6.4 8.6z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="mt-14 pt-6 text-[11px]"
          style={{ borderTop: "1px solid var(--hairline-dark)", color: "var(--muted-dark)" }}
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className={isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide"}>
              © {new Date().getFullYear()} {t.footer.rights}.
            </div>
            <div className={cx(isRTL ? "ff-ar-sans" : "ff-en-sc tracked-wide", "opacity-60")}>
              {isRTL ? "مصر · القاهرة" : "Cairo · Egypt"}
            </div>
          </div>
          {/* Full intellectual-property notice. Rendered as small,
              opacity-suppressed prose so it sits in the footer as
              legal small-print rather than a featured paragraph —
              matches the way the rest of the footer chrome reads. */}
          <p
            className={cx(
              isRTL ? "ff-ar-sans" : "ff-en-body",
              "mt-3 max-w-3xl leading-relaxed opacity-80",
            )}
          >
            {t.footer.ipNotice}
          </p>
        </div>
      </div>
    </footer>
  );
};
